import {
  MODAL_NEW_GENERATE_PROJECT,
  MODAL_UPGRADE_PLAN,
} from '@components/App/Modal/constants'
import { AIActions } from '@store/reducers/ai'
import { openModal } from '@storeOld/reducers/ui'
import { useMemo } from 'react'
import { useDispatch } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useArtboardMode, useProjectAccess } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { PROJECT_SETTINGS_PERMISSION } from '@/permissions/project/definitions'
import { pickRandomListItem } from '@/utils/random'
import { useTranslateHandler } from '../../hooks'
import useChangeColorHandler from '../../hooks/useChangeColorHandler'
import { useGenerateSeoTags } from '../../hooks/useGenerateSeoTags'
import { COLOR_PLACEHOLDERS, type TranslationLanguage } from './constants'

export const useAiMenu = () => {
  const dispatch = useDispatch()

  const { seoMode } = useArtboardMode()
  const disabled = useArtBoardDisabled()

  const { hasNotAvailableAccessBadge: colorsLocked, badgeType: colorsBadge } =
    useProjectAccess(PROJECT_SETTINGS_PERMISSION.AI_COLOR_CHANGE)

  const changeColors = useChangeColorHandler()
  const translate = useTranslateHandler()
  const generateSeo = useGenerateSeoTags()

  const placeholder = useMemo(() => pickRandomListItem(COLOR_PLACEHOLDERS), [])

  // OCT-5102: inside a project the sitemap lands as a new section of it, the
  // way File → Import does, so the modal must not navigate away.
  const onGenerate = () => {
    dispatch(
      openModal({
        modalType: MODAL_NEW_GENERATE_PROJECT,
        modalProps: { intoOpenProject: true },
      })
    )
  }

  const onChangeColors = (prompt: string) => {
    changeColors(prompt)
  }

  const onTranslate = (language: TranslationLanguage) => {
    translate(language)
  }

  // Same entry point as the Sparkles button in the footer: the saga opens the
  // chat sidebar itself and loads the history.
  const onAssistant = () => {
    dispatch(AIActions.startChat({}))
  }

  const onSeoTags = () => {
    generateSeo()
  }

  // The link item opens the docs itself; only the metric is ours.
  const onConnectAgents = () => {
    sendGtmEvent(GtmTriggerName.AI_PROJECT_CONNECT_AGENTS)
  }

  const onUpgrade = () => {
    dispatch(openModal(MODAL_UPGRADE_PLAN))
  }

  return {
    actions: {
      onAssistant,
      onChangeColors,
      onConnectAgents,
      onGenerate,
      onSeoTags,
      onTranslate,
      onUpgrade,
    },
    colorsBadge,
    colorsLocked,
    disabled,
    placeholder,
    seoMode,
  }
}
